import { useEffect, useRef, useState } from "react";
import Button from '@components/legacy/core/Button';
import { useTranslations } from "../../../context/TranslationContext";
import CheckboxField from "../../../core/CheckboxField";
import Icon from "../../../core/Icon";
import ModalComponent from "../../../ModalComponent";
import AlpacaForm from "../../../AlpacaForm";
import fetchCustom from "../../../../wp_helpers/fetchCustom";
import getResponseJson from "../../../../wp_helpers/getResponseJson";

/**
 * Renders both the "Add new video" and the per-question "Edit" trigger + modal: they share the
 * same form schema, validation and Alpaca wiring, and only differ in how the modal is prefilled
 * and where the save request goes.
 *
 * mode="create" (default): a blank form, saved with a POST to the freshly recorded attachment's
 * own urn (the `{video_attachment_urn}` placeholder in `create_attachment_url` is substituted with
 * the recorder widget's `taAttachmentUrn` right before saving - see `saveVideo`).
 * mode="edit": the form is prefilled from `prefillFormData` (the existing question's
 * `video_question_form_data`, already available from the listing/detail endpoints) and saved with
 * a PUT to the fixed `editUrl` - no urn substitution needed since the attachment being edited is
 * already known.
 */
const NewVideoQuestionButton = ({
    createVideoQuestionFormApi,
    getVideoQuestions,
    mode = 'create',
    editUrl,
    prefillFormData,
    showScopeChoice = false,
}) => {
    const { t } = useTranslations();
    const isEditMode = mode === 'edit';
    const [showModal, setShowModal] = useState(false);
    const [formSchema, setFormSchema] = useState({});
    const [saveVideoQuestionApi, setSaveVideoQuestionApi] = useState('');
    // Create-only scope choice: default off - Personal. Scope flips
    // afterwards go through the card's own "Share"/"Make personal" control, not
    // this form, so the checkbox only matters on the create path.
    const [shareWithCompany, setShareWithCompany] = useState(false);
    const formRef = useRef();

    // Addpipe's background-replacement pipeline reassigns its internal stream variable
    // to the processed canvas stream and stashes the raw camera in a closure variable
    // its own remove() never stops (verified against their client source) — so with a
    // background image active, no amount of recorder teardown reaches the raw camera.
    // The only airtight release: while OUR modal is open, wrap getUserMedia and track
    // every stream it hands out; stop them all on close.
    const trackedStreamsRef = useRef([]);
    const originalGetUserMediaRef = useRef(null);
    const startTrackingCameraStreams = () => {
        if (originalGetUserMediaRef.current || !navigator.mediaDevices?.getUserMedia) return;
        const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
        originalGetUserMediaRef.current = original;
        navigator.mediaDevices.getUserMedia = (...args) =>
            original(...args).then((stream) => {
                trackedStreamsRef.current.push(stream);
                return stream;
            });
    };
    const stopTrackedCameraStreams = () => {
        if (originalGetUserMediaRef.current) {
            navigator.mediaDevices.getUserMedia = originalGetUserMediaRef.current;
            originalGetUserMediaRef.current = null;
        }
        trackedStreamsRef.current.forEach((stream) => {
            stream.getTracks().forEach((track) => track.stop());
        });
        trackedStreamsRef.current = [];
    };

    const handleOpenClick = () => {
        startTrackingCameraStreams();
        setShowModal(true);
    };

    // Closing the modal only unmounts the recorder's DOM — Addpipe's MediaRecorder
    // and the camera/mic tracks it acquired keep running (Chrome keeps showing the
    // tab-is-recording indicator). Two-layer teardown, scoped to THIS modal only:
    // 1. recorder.remove() — Addpipe's own teardown (stops MediaRecorder + its
    //    localStream) — when its registry has the entry.
    // 2. Stop every live MediaStream attached to the modal's <video> elements —
    //    works regardless of Addpipe's registry timing or stale re-inserts, since
    //    the camera preview always holds the live stream as srcObject.
    const modalRef = useRef();
    const releaseModalRecorders = () => {
        if (!modalRef.current) return;
        modalRef.current.querySelectorAll('[id^="video-recorder-container-"]').forEach((container) => {
            try {
                window.PipeSDK?.recorders?.[container.id]?.remove?.();
            } catch (e) {
                // Best-effort: a recorder that never fully initialized has nothing to release.
            }
        });
        modalRef.current.querySelectorAll('video').forEach((video) => {
            const stream = video.srcObject;
            if (stream && typeof stream.getTracks === 'function') {
                stream.getTracks().forEach((track) => track.stop());
                video.srcObject = null;
            }
        });
        stopTrackedCameraStreams();
    };

    const handleCloseModal = () => {
        releaseModalRecorders();
        setShowModal(false);
        setShareWithCompany(false);
    };

    // Editing prefills the video_recorder_widget with the existing video, which hides its
    // recorder and just shows a preview (see handleVideoPreviews in alpaca_custom.js). Dropping
    // `video_question` from the form's initial data forces AlpacaForm to remount the field fresh,
    // so it behaves exactly like the "Add new video" case and shows the recorder again.
    const handleRecordNewVideo = () => {
        setFormSchema((previousSchema) => {
            if (!previousSchema?.data?.video_question) return previousSchema;
            const { video_question, ...restData } = previousSchema.data;
            return { ...previousSchema, data: restData };
        });
    };

    const saveVideo = async () => {
        const _formData = {
            form_json: formRef.current.getFormData(),
        };
        let saveUrl = editUrl;
        let method = 'PUT';
        if (!isEditMode) {
            const formContainer = formRef.current.getContainer();
            const attachmentUrn = formContainer?.childrenById?.video_question?.taAttachmentUrn;
            saveUrl = saveVideoQuestionApi.replace('{video_attachment_urn}', attachmentUrn);
            method = 'POST';
            if (showScopeChoice) {
                _formData.scope = shareWithCompany ? 'shared' : 'personal';
            }
        }
        const response = await fetchCustom(saveUrl, {
            method,
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(_formData),
        });
        const responseJson = await getResponseJson(response);
        if (response.ok) {
            handleCloseModal();
            if (typeof getVideoQuestions === 'function') {
                getVideoQuestions();
                if (_formData.form_json.video_question) {
                    setTimeout(() => {
                        getVideoQuestions(); // Fetch again after a while because the video takes time to process
                    }, 2500);
                }
            }
        }
    };

    const handleSaveVideo = async () => {
        const formContainer = formRef.current.getContainer();
        formContainer.refreshValidationState(true, async () => {
            if (!formContainer.isValid(true)) {
                formContainer.focus();
                notification(t('Please fix the errors and try again.'));
                return;
            };
            saveVideo();
        });
    };

    const getFormSchema = async () => {
        const response = await fetchCustom(createVideoQuestionFormApi);
        const responseJson = await getResponseJson(response);
        if (response.ok) {
            const {
                form,
                create_attachment_url: _saveVideoQuestionApi,
            } = responseJson;
            const _formSchema = form ? JSON.parse(form) : {};
            if (isEditMode && prefillFormData) {
                _formSchema.data = { ..._formSchema.data, ...prefillFormData };
            }
            setSaveVideoQuestionApi(_saveVideoQuestionApi);
            setFormSchema(_formSchema);
        }
    };

    useEffect(() => {
        if (showModal) {
            getFormSchema();
        }
    }, [showModal]);

    const hasExistingVideo = Boolean(formSchema?.data?.video_question?.video_url);

    return (
        <>
            {isEditMode ? (
                <Button type="link-secondary" onClick={handleOpenClick}>
                    <Icon value="edit-02" />
                    {t('Edit')}
                </Button>
            ) : (
                <Button type="primary" onClick={handleOpenClick}>
                    <Icon value="plus" />
                    {t('Add new video')}
                </Button>
            )}
            <ModalComponent show={showModal} closeModal={handleCloseModal}>
                <div className="overlay" ref={modalRef}>
                    <div className="overlay-header">
                        <h3>{isEditMode ? t('Edit video question') : t('Add new video')}</h3>
                    </div>
                    {formSchema.schema &&
                        <>
                            <div className="overlay-main">
                                {isEditMode && hasExistingVideo &&
                                    <div className="margin-vertical-s text-right">
                                        <Button type="link-secondary" onClick={handleRecordNewVideo}>
                                            {t('Record a new video')}
                                        </Button>
                                    </div>
                                }
                                <AlpacaForm
                                    ref={formRef}
                                    formSchema={formSchema}
                                />
                                {!isEditMode && showScopeChoice &&
                                    <div className="margin-vertical-s">
                                        <CheckboxField
                                            checkboxId="share-video-question-with-company"
                                            label={t('Share with the whole company')}
                                            isChecked={shareWithCompany}
                                            onChange={(e) => setShareWithCompany(e.target.checked)}
                                        />
                                    </div>
                                }
                            </div>
                            <div className="overlay-footer">
                                <div className="btn-container">
                                    <Button type="primary" onClick={handleSaveVideo}>{t('Save')}</Button>
                                </div>
                            </div>
                        </>
                    }
                </div>
            </ModalComponent>
        </>
    );
};

export default NewVideoQuestionButton;